import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Heart, Copy } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import portfolioData from '../data/mock';

const Footer = () => {
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();

  // Function to copy email to clipboard
  const copyEmail = async () => {
    const email = portfolioData.personalInfo.email;
    try {
      await navigator.clipboard.writeText(email);
      toast({
        title: "Email Copiado",
        description: `${email} copiado al portapapeles`,
      });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({
        title: "Email Copiado",
        description: `${email} copiado al portapapeles`,
      });
    }
  };

  // Function to copy phone to clipboard
  const copyPhone = async () => {
    const phone = portfolioData.personalInfo.phone;
    try {
      await navigator.clipboard.writeText(phone);
      toast({
        title: "Teléfono Copiado",
        description: `${phone} copiado al portapapeles`,
      });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = phone;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({
        title: "Teléfono Copiado",
        description: `${phone} copiado al portapapeles`,
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-stone-900 border-t border-stone-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Brand & Description */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-yellow-600 mb-4">
                José Manuel Olvera
              </h3>
              <p className="text-stone-400 leading-relaxed">
                Especialista en transformación agronómica con 37+ años de experiencia 
                liderando innovación en agricultura de precisión y regenerativa.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <a 
                href={portfolioData.personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-stone-800 rounded-lg hover:bg-yellow-600 transition-colors group"
              >
                <Linkedin size={20} className="text-stone-400 group-hover:text-stone-900" />
              </a>
              <button 
                onClick={copyEmail}
                className="p-3 bg-stone-800 rounded-lg hover:bg-yellow-600 transition-colors group"
                title="Copiar email"
              >
                <Mail size={20} className="text-stone-400 group-hover:text-stone-900" />
              </button>
              <button 
                onClick={copyPhone}
                className="p-3 bg-stone-800 rounded-lg hover:bg-yellow-600 transition-colors group"
                title="Copiar teléfono"
              >
                <Phone size={20} className="text-stone-400 group-hover:text-stone-900" />
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h4 className="text-lg font-bold text-stone-200">
              Enlaces Rápidos
            </h4>
            <nav className="space-y-3">
              {[
                { label: 'Acerca de', href: '#about' },
                { label: 'Experiencia', href: '#experience' },
                { label: 'Habilidades', href: '#skills' },
                { label: 'Proyectos', href: '#projects' },
                { label: 'Contacto', href: '#contact' }
              ].map((link) => (
                <button
                  key={link.label}
                  onClick={() => {
                    const element = document.querySelector(link.href);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="block text-stone-400 hover:text-yellow-600 transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <h4 className="text-lg font-bold text-stone-200">
              Información de Contacto
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-yellow-600 mt-1 flex-shrink-0" />
                <span className="text-stone-400">
                  {portfolioData.personalInfo.location}
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-yellow-600 mt-1 flex-shrink-0" />
                <button 
                  onClick={copyEmail}
                  className="text-stone-400 hover:text-yellow-600 transition-colors flex items-center gap-1"
                  title="Copiar email"
                >
                  {portfolioData.personalInfo.email}
                  <Copy size={12} className="opacity-70" />
                </button>
              </div>
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-yellow-600 mt-1 flex-shrink-0" />
                <button 
                  onClick={copyPhone}
                  className="text-stone-400 hover:text-yellow-600 transition-colors flex items-center gap-1"
                  title="Copiar teléfono"
                >
                  {portfolioData.personalInfo.phone}
                  <Copy size={12} className="opacity-70" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-stone-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-stone-500 text-sm">
              © {currentYear} José Manuel Antonio Olvera Sousa. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-2 text-stone-500 text-sm">
              <span>Hecho con</span>
              <Heart size={14} className="text-yellow-600" />
              <span>para la agricultura del futuro</span>
            </div>
            <button
              onClick={scrollToTop}
              className="text-stone-400 hover:text-yellow-600 transition-colors text-sm"
            >
              Volver al inicio ↑
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;